import { useEffect, useMemo, useState } from "react";
import { listAutomations, type AutomationsResponse, type AutomationRobot } from "../../lib/api.js";

/**
 * Automatizace — přehled našich robotů (cron na serveru) a jejich stavu.
 *
 * Data generuje sběrač `automatizace_status.py` (repo ktm-vlasim) každých
 * 10 minut z logů robotů; kanonický seznam: ktm-shopify-eshop/AUTOMATIZACE.md.
 *
 * 🆕 12. 8. 2026 — přestavěno na přání Víta („ne pátý přes devátý"): nahoře
 * odpovědi na otázky, které se ptá nejčastěji („máme aktuální ceny?"), pod
 * nimi bloky podle toho, čeho se robot týká. Podrobný popis a řádek z logu
 * jsou schované do rozkliknutí — na první pohled má být vidět jen to, jestli
 * je něco v nepořádku.
 *
 * 🔑 NÁLEZ NENÍ PORUCHA. Hlídači (`druh === "hlidac"`) končí kódem 1 ZÁMĚRNĚ,
 * když něco najdou. Do 12. 8. svítily stejně červeně jako spadlý robot, takže
 * `nedodá` s 11 nálezy vypadal jako rozbitý stroj. Teď je to oranžový „nález".
 *
 * ⚠ Sběrač může být starší než tahle stránka (nasazuje se zvlášť) — všechna
 * nová pole se proto čtou přes fallback a bez nich to jen zšedne, nespadne.
 */

type Stav = "chyba" | "pozdni" | "pozor" | "nalez" | "neznamy" | "ok";

const STAVY: Record<Stav, { slovo: string; znak: string; text: string; chip: string; pruh: string }> = {
  chyba:   { slovo: "Chyba",      znak: "×", text: "text-red-700",     chip: "bg-red-50 text-red-700",         pruh: "border-l-red-500" },
  pozdni:  { slovo: "Po termínu", znak: "!", text: "text-red-700",     chip: "bg-red-50 text-red-700",         pruh: "border-l-red-500" },
  pozor:   { slovo: "Pozor",      znak: "!", text: "text-amber-700",   chip: "bg-amber-50 text-amber-800",     pruh: "border-l-amber-500" },
  nalez:   { slovo: "Nález",      znak: "!", text: "text-amber-700",   chip: "bg-amber-50 text-amber-800",     pruh: "border-l-amber-500" },
  neznamy: { slovo: "Bez záznamu", znak: "?", text: "text-zinc-500",   chip: "bg-zinc-100 text-zinc-600",      pruh: "border-l-zinc-300" },
  ok:      { slovo: "V pořádku",  znak: "✓", text: "text-emerald-700", chip: "bg-emerald-50 text-emerald-700", pruh: "border-l-emerald-500" },
};
const HORSI: Stav[] = ["chyba", "pozdni", "pozor", "nalez", "neznamy", "ok"];

/** Bloky stránky. Robot bez známé skupiny spadne do „Ostatní" — nikdy nezmizí. */
const SKUPINY: { id: string; nazev: string; podnadpis: string }[] = [
  { id: "ceny", nazev: "Ceny a sklad", podnadpis: "Za kolik prodáváme a kolik toho máme." },
  { id: "eshop", nazev: "Co vidí zákazník", podnadpis: "Dostupnost, řazení a blokace košíku na kartách." },
  { id: "vyrobce", nazev: "Data od výrobce", podnadpis: "Obsah karet: popisy, fotky, rozkresy, kompatibilita." },
  { id: "hlidaci", nazev: "Hlídači", podnadpis: "Jen hlásí nálezy — sami nic nemění." },
  { id: "obsah", nazev: "Texty pro AI rádce", podnadpis: "Věty a metapole, ze kterých čte AI rádce." },
  { id: "provoz", nazev: "Zálohy a provoz", podnadpis: "Aby bylo co obnovit a widget nespadl." },
  { id: "ostatni", nazev: "Ostatní", podnadpis: "Robot bez zařazení — doplň mu `sk=` ve sběrači." },
];

/**
 * ZNAČKY (17. 8. 2026, přání Víta „chci lépe porovnat, co je KTM a co ostatní").
 *
 * ⚠ Vědomě to NENÍ další dělící čára v obsahu — bloky zůstávají podle PRÁCE
 * („co je rozbité" se ptáš každý den, „co dělá Acerbis" jednou za čas).
 * Značka je jen ŠTÍTEK + FILTR přes ně. KTM tvoří dvě třetiny robotů, proto
 * se u něj štítek nekreslí — vypadal by na skoro každé kartě jako šum.
 */
const ZNACKY: { id: string; nazev: string; chip: string }[] = [
  { id: "ktm", nazev: "KTM", chip: "bg-orange-50 text-orange-700" },
  { id: "acerbis", nazev: "Acerbis", chip: "bg-sky-50 text-sky-700" },
  { id: "b2bike", nazev: "B2Bike", chip: "bg-teal-50 text-teal-700" },
  { id: "motorex", nazev: "Motorex", chip: "bg-rose-50 text-rose-700" },
  { id: "risk", nazev: "Risk Racing", chip: "bg-indigo-50 text-indigo-700" },
  { id: "vse", nazev: "napříč značkami", chip: "bg-zinc-100 text-zinc-600" },
];
const znackaRobota = (r: AutomationRobot): string =>
  ZNACKY.some((z) => z.id === r.znacka) ? (r.znacka as string) : "ktm";
const znackaInfo = (id: string) => ZNACKY.find((z) => z.id === id);

/** Horní pás: otázka lidsky → ze kterých robotů se na ni odpovídá. */
const PAS: { otazka: string; ids: string[]; hlavni?: string }[] = [
  { otazka: "Máme aktuální ceny?", ids: ["cenik", "ftp-sync"], hlavni: "cenik" },
  { otazka: "Máme aktuální sklad?", ids: ["sklad", "warehouse"], hlavni: "sklad" },
  { otazka: "Sedí dostupnost na eshopu?", ids: ["fanshop", "acerbis"], hlavni: "fanshop" },
  { otazka: "Máme čerstvá data od výrobce?", ids: ["pim-sync", "catalog-sync"], hlavni: "catalog-sync" },
  { otazka: "Našli hlídači něco?", ids: ["nedoda", "nove-dily", "publikace", "size-siblings", "snapshot-diff"] },
  { otazka: "Máme zálohy a běží provoz?", ids: ["db-backup", "zaloha-struktura", "sync-back", "cert-renew"], hlavni: "db-backup" },
];

const CIL: Record<string, string> = {
  eshop: "na eshop", katalog: "do katalogu", hlasi: "nic — jen hlásí",
  zaloha: "do záloh", provoz: "server",
};

const skupinaRobota = (r: AutomationRobot): string =>
  SKUPINY.some((s) => s.id === r.skupina) ? (r.skupina as string) : "ostatni";

function stariH(r: AutomationRobot, ted: number): number | null {
  if (!r.lastRunUtc) return null;
  return (ted - new Date(r.lastRunUtc).getTime()) / 3_600_000;
}

function poTerminu(r: AutomationRobot, ted: number): boolean {
  const h = stariH(r, ted);
  return !!r.mezH && h !== null && h > r.mezH;
}

/** Stav jednoho robota. Nález hlídače NENÍ porucha — proto vlastní stupeň. */
function stav(r: AutomationRobot, ted: number): Stav {
  if (r.ok === false) return r.druh === "hlidac" ? "nalez" : "chyba";
  if (r.ok === null) return "neznamy";
  // certifikát widgetu platí jen 6 dní; sběrač zčervená až pod 2 dny,
  // což je pozdě na to, aby si toho někdo všiml — na 2 dnech varujeme
  const dny = /platí ještě (\d+)/.exec(r.note ?? "");
  if (dny && Number(dny[1]) <= 2) return "pozor";
  if (poTerminu(r, ted)) return "pozdni";
  return "ok";
}

const horsi = (a: Stav, b: Stav): Stav => (HORSI.indexOf(a) < HORSI.indexOf(b) ? a : b);
const pozornost = (r: AutomationRobot, ted: number): boolean => stav(r, ted) !== "ok";

/** 1 nález · 2–4 nálezy · 5+ nálezů */
const pocet = (n: number, a: string, b: string, c: string): string =>
  `${n} ${n === 1 ? a : n < 5 ? b : c}`;

function nalezu(r: AutomationRobot): number {
  const m = /(\d+)\s*nov/.exec(r.note ?? "");
  return m ? Number(m[1]) : 0;
}

function vek(h: number | null): string {
  if (h === null) return "";
  if (h < 1) return `před ${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `před ${Math.round(h)} h`;
  const d = Math.round(h / 24);
  return `před ${d} ${d === 1 ? "dnem" : "dny"}`;
}

/** „dnes 7:31" místo „2026-08-12 05:31:18" — a v našem čase, ne v UTC. */
function kdy(r: AutomationRobot, ted: number): string {
  if (!r.lastRunUtc) return "—";
  const d = new Date(r.lastRunUtc);
  const cas = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const dnes = new Date(ted);
  dnes.setHours(0, 0, 0, 0);
  const den = new Date(d);
  den.setHours(0, 0, 0, 0);
  const rozdil = Math.round((dnes.getTime() - den.getTime()) / 86_400_000);
  if (rozdil === 0) return `dnes ${cas}`;
  if (rozdil === 1) return `včera ${cas}`;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${cas}`;
}

/** Z poslední zprávy v logu udělá jednu čitelnou větu. */
function vysledek(r: AutomationRobot): string {
  if (r.id === "cert-renew") return r.note;
  if (r.druh === "hlidac") {
    const m = /(\d+)\s*nov/.exec(r.note ?? "");
    if (m) return `${pocet(Number(m[1]), "nový nález", "nové nálezy", "nových nálezů")} k prověření`;
    return r.ok === true ? "bez nálezu" : r.note;
  }
  const t = (r.note ?? "").replace(/^\[?[\d.\-: ]{8,}\]?\s*/, "").replace(/^-+\s*|\s*-+$/g, "").trim();
  if (/^(konec\.?|KONEC|.*— konec)$/i.test(t)) return "doběhlo bez chyby";
  return t || "—";
}

export function AutomationsPage(): JSX.Element {
  const [data, setData] = useState<AutomationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "vse" · "pozor" (jen k prověření) · "bezauditu" (Vítův pracovní seznam
  // na projíždění robotů jeden po druhém — pilulka Audit, 17. 8. 2026)
  const [filtr, setFiltr] = useState<"vse" | "pozor" | "bezauditu">("vse");
  // Filtr značky je SAMOSTATNÝ — dá se kombinovat s tím nahoře
  // („jen co potřebuje pozornost“ × „Acerbis“). `null` = neomezovat.
  const [znacka, setZnacka] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    listAutomations()
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((e: unknown) => {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted = false;
    };
  }, []);

  // čas snímku, ne „teď" — stáří počítáme proti tomu, co sběrač viděl
  const ted = useMemo(
    () => (data?.generatedAt ? new Date(data.generatedAt).getTime() : Date.now()),
    [data?.generatedAt],
  );
  const roboti = data?.roboti ?? [];
  const potize = roboti.filter((r) => pozornost(r, ted)).length;
  const auditovano = roboti.filter((r) => !!r.audit).length;
  const najdi = (id: string): AutomationRobot | undefined => roboti.find((r) => r.id === id);
  const projde = (r: AutomationRobot): boolean => {
    // 🔑 Nález Víta 17. 8.: „dám KTM a zmizí mi sklad".
    // Robot označený `vse` (sklad, slevy, zálohy) se týká KAŽDÉ značky —
    // proto při filtru na konkrétní značku zůstává vidět. Jinak filtr lže:
    // tvářil by se, že o sklad KTM se nikdo nestará. Chip „napříč značkami"
    // naopak ukáže JEN ty společné.
    if (znacka && znacka !== "vse" && !["vse", znacka].includes(znackaRobota(r)))
      return false;
    if (znacka === "vse" && znackaRobota(r) !== "vse") return false;
    return filtr === "pozor" ? pozornost(r, ted) : filtr === "bezauditu" ? !r.audit : true;
  };
  // rozpad pro počítadlo: kolik robotů je čistě KTM a kolik ostatní značky
  // (roboti „napříč“ — sklad, slevy, zálohy — se počítají zvlášť, aby to
  //  nevypadalo, že se o cizí značky staráme víc, než se staráme)
  const pocetZn = (id: string) => roboti.filter((r) => znackaRobota(r) === id).length;
  const cizichZnacek = roboti.filter(
    (r) => !["ktm", "vse"].includes(znackaRobota(r)),
  ).length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">Automatizace</h1>
        <p className="mt-1 text-sm text-zinc-500 max-w-2xl">
          Nejdřív odpovědi na to, co potřebuješ vědět. Podrobnosti až pod tím, po blocích —
          a jen když si o ně řekneš. Stav se obnovuje každých 10 minut z logů na serveru.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-6">
          Chyba načtení: {error}
        </div>
      )}

      {data?.missing && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 mb-6">
          Sběrač stavu zatím nic nezapsal — počkej na příští 10minutový cyklus.
        </div>
      )}

      {data && !data.missing && data.stale && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 mb-6">
          ⚠ Data jsou {data.ageMinutes} min stará — sběrač stavu se odmlčel
          (má běžet každých 10 minut). Zkontroluj cron na serveru.
        </div>
      )}

      {!data && !error && <div className="text-sm text-zinc-500">Načítám…</div>}

      {roboti.length > 0 && (
        <>
          <div className="grid gap-3 mb-6 sm:grid-cols-2 lg:grid-cols-3">
            {PAS.map((d) => (
              <Dlazdice key={d.otazka} d={d} najdi={najdi} ted={ted} />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {(
              [
                { k: "vse", t: "Vše" },
                { k: "pozor", t: "Jen co potřebuje pozornost" },
                { k: "bezauditu", t: "Bez auditu" },
              ] as const
            ).map((b) => (
              <button
                key={b.t}
                type="button"
                onClick={() => setFiltr(b.k)}
                aria-pressed={filtr === b.k}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  filtr === b.k
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                }`}
              >
                {b.t}
              </button>
            ))}
            <span className="ml-auto text-xs text-zinc-500 tabular-nums">
              {roboti.length} robotů celkem · {roboti.length - potize} v pořádku · {potize} k prověření
              {" · "}auditováno {auditovano} z {roboti.length}
            </span>
          </div>

          {/* Druhý řádek — ZNAČKA. Vědomě samostatný filtr, ne další sekce:
              bloky drží dělení podle práce, tohle je pohled napříč nimi. */}
          <div className="flex flex-wrap items-center gap-2 mb-6 -mt-3">
            <span className="text-[11px] uppercase tracking-wide text-zinc-400 mr-1">
              Značka
            </span>
            <button
              type="button"
              onClick={() => setZnacka(null)}
              aria-pressed={znacka === null}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                znacka === null
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
              }`}
            >
              Všechny
            </button>
            {ZNACKY.filter((z) => pocetZn(z.id) > 0).map((z) => (
              <button
                key={z.id}
                type="button"
                onClick={() => setZnacka(znacka === z.id ? null : z.id)}
                aria-pressed={znacka === z.id}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  znacka === z.id
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                }`}
              >
                {z.nazev}{" "}
                <span className="tabular-nums opacity-60">{pocetZn(z.id)}</span>
              </button>
            ))}
            <span className="ml-auto text-xs text-zinc-500 tabular-nums">
              KTM {pocetZn("ktm")} · ostatní značky {cizichZnacek} · napříč {pocetZn("vse")}
            </span>
          </div>

          {znacka && znacka !== "vse" && (
            <div className="-mt-4 mb-6 text-xs text-zinc-500">
              Vidíš roboty značky{" "}
              <b className="font-medium text-zinc-700">{znackaInfo(znacka)?.nazev}</b> a k
              tomu {pocetZn("vse")} robotů, kteří jedou{" "}
              <b className="font-medium text-zinc-700">napříč všemi značkami</b> (sklad,
              slevy, zálohy) — týkají se jí taky.
            </div>
          )}

          <div className="space-y-7">
            {SKUPINY.map((sk) => {
              const cast = roboti.filter((r) => skupinaRobota(r) === sk.id);
              if (cast.length === 0) return null;
              const videt = cast.filter(projde);
              if (videt.length === 0) return null;
              // ⚠ Počítá se z VIDĚT, ne z `cast` (nález Víta 17. 8.: hlavička
              //   hlásila „11 robotů“, ale pod ní byli po filtru čtyři).
              const k_provereni = videt.filter((r) => pozornost(r, ted)).length;
              const nejhorsi = videt.map((r) => stav(r, ted)).reduce(horsi, "ok");
              return (
                <section key={sk.id}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-zinc-200 pt-3 mb-2.5">
                    <h2 className="text-[15px] font-semibold text-zinc-900">{sk.nazev}</h2>
                    <span className="text-xs text-zinc-500">{sk.podnadpis}</span>
                    <span className="ml-auto text-xs text-zinc-500">
                      {videt.length} {videt.length < 5 ? "roboti" : "robotů"}
                      {videt.length !== cast.length && (
                        <span className="text-zinc-400"> z {cast.length}</span>
                      )}{" "}
                      ·{" "}
                      {k_provereni === 0 ? (
                        "vše v pořádku"
                      ) : (
                        <b className={`font-semibold ${STAVY[nejhorsi].text}`}>{k_provereni} k prověření</b>
                      )}
                    </span>
                  </div>
                  <div className="grid gap-2.5 lg:grid-cols-2">
                    {videt.map((r) => (
                      <Karta key={r.id} r={r} ted={ted} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      <div className="mt-8 text-xs text-zinc-400 space-y-1">
        <p>
          Kanonický seznam robotů a plán rozvoje: <code>ktm-shopify-eshop/AUTOMATIZACE.md</code>{" "}
          (repo ktm-vlasim). Roboti běží cronem na tomto serveru — nezávisí na žádném PC.
        </p>
        {data?.generatedAt && <p>Snímek: {new Date(data.generatedAt).toLocaleString("cs-CZ")}</p>}
      </div>
    </div>
  );
}

/** Odpověď na jednu otázku nahoře — poskládaná z několika robotů. */
function Dlazdice({
  d,
  najdi,
  ted,
}: {
  d: { otazka: string; ids: string[]; hlavni?: string };
  najdi: (id: string) => AutomationRobot | undefined;
  ted: number;
}): JSX.Element | null {
  const cast = d.ids.map(najdi).filter((r): r is AutomationRobot => !!r);
  const prvni = cast[0];
  if (!prvni) return null; // sběrač zatím žádného z těchhle robotů nezná

  const nejhorsi = cast.map((r) => stav(r, ted)).reduce(horsi, "ok");
  const samiHlidaci = cast.every((r) => r.druh === "hlidac");
  const soucet = cast.reduce((s, r) => s + nalezu(r), 0);
  const spatne = cast.filter((r) => stav(r, ted) !== "ok");

  let slovo: string;
  let znak: string;
  let barva: string;
  let pruh: string;
  let radek: string;

  if (samiHlidaci) {
    const cisto = soucet === 0;
    slovo = cisto ? "Čisto" : pocet(soucet, "nález", "nálezy", "nálezů");
    znak = cisto ? "✓" : "!";
    barva = cisto ? STAVY.ok.text : STAVY.nalez.text;
    pruh = cisto ? STAVY.ok.pruh : STAVY.nalez.pruh;
    radek =
      cast.filter((r) => nalezu(r) > 0).map((r) => `${r.zkratka ?? r.label}: ${nalezu(r)}`).join(" · ") ||
      "všichni hlídači bez nálezu";
  } else {
    const s = STAVY[nejhorsi];
    barva = s.text;
    pruh = s.pruh;
    znak = s.znak;
    slovo =
      nejhorsi === "ok" ? "Aktuální"
      : nejhorsi === "pozdni" ? "Zpoždění"
      : nejhorsi === "neznamy" ? "Neznámo"
      : s.slovo;
    if (spatne.length === 0) {
      const h = najdi(d.hlavni ?? "") ?? prvni;
      radek = `${h.zkratka ?? h.label}: ${kdy(h, ted)} · ${vek(stariH(h, ted))}`;
    } else {
      radek = spatne
        .map((r) => {
          const co = r.lastRunUtc
            ? `${kdy(r, ted)} (${vek(stariH(r, ted))})`
            : r.id === "cert-renew"
              ? ((r.note ?? "").split(";")[0] ?? "").replace(/^certifikát /, "")
              : "v logu žádný běh";
          return `${r.zkratka ?? r.label}: ${co}`;
        })
        .join(" · ");
    }
  }

  return (
    <div className={`bg-white rounded-lg ring-1 ring-zinc-200 shadow-card border-l-[3px] ${pruh} px-4 py-3`}>
      <div className="text-xs text-zinc-500">{d.otazka}</div>
      <div className={`mt-1.5 flex items-baseline gap-2 ${barva}`}>
        <span className="text-sm leading-none">{znak}</span>
        <span className="text-xl font-semibold tracking-tight leading-tight">{slovo}</span>
      </div>
      <div className="mt-1 text-xs text-zinc-600 tabular-nums">{radek}</div>
      <div className="mt-2 pt-2 border-t border-zinc-100 text-[11px] text-zinc-400">
        {cast.map((r) => r.label).join(" · ")}
      </div>
    </div>
  );
}

/** Jeden robot. Zavřená karta říká stav; popis a log až po rozkliknutí. */
function Karta({ r, ted }: { r: AutomationRobot; ted: number }): JSX.Element {
  const [otevreno, setOtevreno] = useState(false);
  const st = stav(r, ted);
  const s = STAVY[st];
  const h = stariH(r, ted);
  const pozdni = poTerminu(r, ted);
  const zn = znackaInfo(znackaRobota(r));

  return (
    /*
      🧨 `min-w-0` NENÍ zbytečné. Karta je `<button>` a ten má jako položka
      mřížky `min-width: auto` — tedy se NESMRSKNE pod šířku svého obsahu,
      i když má `w-full`. Vnitřní mřížka `1fr 1fr auto` ji roztáhla na 809 px
      ve 324px sloupci a stránka přetékala o 469 px (změřeno 15. 8. 2026).
      Obyčejný `<div>` by se zmenšil sám, `<button>` ne.
    */
    <button
      type="button"
      onClick={() => setOtevreno((x) => !x)}
      aria-expanded={otevreno}
      className={`w-full min-w-0 text-left bg-white rounded-lg ring-1 ring-zinc-200 hover:ring-zinc-300 shadow-card border-l-[3px] ${s.pruh} px-3.5 py-3`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-zinc-900">{r.label}</span>
            {/* štítek značky — u KTM se NEKRESLÍ (je to dvě třetiny robotů,
                byl by na skoro každé kartě a nic by neřekl) */}
            {zn && zn.id !== "ktm" && (
              <span className={`shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded ${zn.chip}`}>
                {zn.nazev}
              </span>
            )}
          </div>
          {r.co && <div className="text-xs text-zinc-500 mt-0.5">{r.co}</div>}
        </div>
        {/* pilulka „Audit + datum" — kdy robot prošel detailním rozborem;
            karta bez ní = ještě nerozpitváno (filtr „Bez auditu") */}
        {r.audit && (
          <span
            className="ml-auto shrink-0 text-[11px] font-medium px-2 py-0.5 rounded bg-violet-50 text-violet-700"
            title={r.auditDoc ? `kanon: ${r.auditDoc}` : undefined}
          >
            Audit {datumCz(r.audit)}
          </span>
        )}
        <span className={`${r.audit ? "" : "ml-auto "}shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded ${s.chip}`}>
          {st === "nalez" ? pocet(nalezu(r), "nález", "nálezy", "nálezů") : s.slovo}
        </span>
      </div>

      <div className="mt-2 pt-2 border-t border-zinc-100 grid grid-cols-[1fr_1fr_auto] gap-x-3">
        <Pole stitek="Plán" hodnota={r.plan} />
        {r.lastRunUtc ? (
          <Pole
            stitek="Naposled"
            hodnota={`${kdy(r, ted)} `}
            dovetek={vek(h)}
            zvyraznit={pozdni}
          />
        ) : (
          <Pole stitek="Naposled" hodnota={r.id === "cert-renew" ? "průběžně (timer)" : "—"} />
        )}
        <Pole stitek="Zapisuje" hodnota={CIL[r.cil ?? ""] ?? "—"} vpravo />
      </div>

      <div className="mt-1.5 text-[11px] text-zinc-500 truncate">{vysledek(r)}</div>

      {otevreno && (
        <div className="mt-2.5 pt-2.5 border-t border-dashed border-zinc-200 space-y-2">
          <p className="text-xs text-zinc-600 max-w-prose">{r.popis}</p>
          <div className="rounded bg-zinc-50 ring-1 ring-zinc-100 px-2 py-1.5 text-[11px] text-zinc-600 break-words">
            {r.note || "—"}
          </div>
          {r.audit && (
            <div className="text-[11px] text-violet-700">
              audit {datumCz(r.audit)}
              {r.auditDoc && <span className="text-zinc-400"> · kanon: {r.auditDoc} (repo ktm-vlasim)</span>}
            </div>
          )}
          <div className="text-[11px] text-zinc-400 break-all">log: {r.log}</div>
        </div>
      )}
    </button>
  );
}

/** „2026-08-17" → „17. 8. 2026" */
function datumCz(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])}. ${Number(m[2])}. ${m[1]}`;
}

function Pole({
  stitek,
  hodnota,
  dovetek,
  zvyraznit,
  vpravo,
}: {
  stitek: string;
  hodnota: string;
  dovetek?: string;
  zvyraznit?: boolean;
  vpravo?: boolean;
}): JSX.Element {
  return (
    <div className={`min-w-0 ${vpravo ? "text-right" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">{stitek}</div>
      <div className={`text-xs tabular-nums ${zvyraznit ? "text-red-700 font-semibold" : "text-zinc-700"}`}>
        {hodnota}
        {dovetek && <span className="text-zinc-400">{dovetek}</span>}
      </div>
    </div>
  );
}
